import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import './styles/WeeklySnapshotModal.css';

const STATUS_CLASS = { outstanding: 'wsnap-out', confirmed: 'wsnap-conf', fulfilled: 'wsnap-ful' };
const STATUS_LABEL = { outstanding: 'Outstanding', confirmed: 'Confirmed', fulfilled: 'Fulfilled' };

const rowLabel = (r) =>
  `${r.name}${r.bezel ? ` (${r.bezel === 'white' ? 'White' : r.bezel === 'red' ? 'Red' : 'Black'})` : ''}`;

function WeeklySnapshotModal({ weekKey, weekLabel, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/forecast/overview');
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Snapshot load error:', err);
      setError('Failed to load the snapshot.');
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Index of the selected week's column in the combined forecast. Exact match
  // first; if a grid derived the key slightly differently, fall back to the
  // week whose Monday is closest.
  let wi = -1;
  if (data && data.weeks.length) {
    wi = data.weeks.indexOf(weekKey);
    if (wi < 0 && weekKey) {
      const t = new Date(weekKey + 'T00:00:00').getTime();
      let best = 0, bestDiff = Infinity;
      data.weeks.forEach((w, i) => {
        const diff = Math.abs(new Date(w + 'T00:00:00').getTime() - t);
        if (diff < bestDiff) { bestDiff = diff; best = i; }
      });
      wi = best;
    }
    if (wi < 0) wi = 0;
  }

  // Build the per-company lists for just this week.
  const companies = [];
  let grandTotal = 0;
  if (data && wi >= 0) {
    data.customers.forEach(c => {
      const items = c.rows
        .map(r => ({ label: rowLabel(r), code: r.modelCode, qty: (r.weekly || [])[wi] || 0, status: (r.weeklyStatus || [])[wi] || null }))
        .filter(x => x.qty);
      if (!items.length) return;
      const total = items.reduce((s, x) => s + x.qty, 0);
      grandTotal += total;
      companies.push({ company: c.company, items, total });
    });
  }

  const generated = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });

  return (
    <div className="wsnap-overlay" onClick={onClose}>
      <div className="wsnap-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wsnap-header wsnap-no-print">
          <div>
            <div className="wsnap-title">Sales Forecast — Weekly Snapshot of {weekLabel}</div>
            <div className="wsnap-subtitle">All Companies · this week only</div>
          </div>
          <div className="wsnap-actions">
            <button className="srf-btn srf-btn-secondary" onClick={() => window.print()}>🖨 Print / Save PDF</button>
            <button className="wsnap-close" onClick={onClose} title="Close">×</button>
          </div>
        </div>

        <div className="wsnap-body">
          <div className="wsnap-print-head">
            <strong>Sales Forecast — Weekly Snapshot of {weekLabel}</strong> · All Companies
          </div>

          {error && <div className="wsnap-msg">{error}</div>}
          {!data && !error && <div className="wsnap-msg">Loading…</div>}
          {data && companies.length === 0 && (
            <div className="wsnap-msg">No forecast scheduled for this week ({weekLabel}).</div>
          )}

          {companies.length > 0 && (
            <div className="wsnap-grandtotal">Week total · all companies: <strong>{grandTotal.toLocaleString()}</strong></div>
          )}

          {companies.map(c => (
            <div key={c.company} className="wsnap-company">
              <div className="wsnap-company-head">
                <span className="wsnap-company-name">{c.company}</span>
                <span className="wsnap-company-total">{c.total.toLocaleString()}</span>
              </div>
              <div className="wsnap-list">
                {c.items.map((x, i) => (
                  <div key={i} className="wsnap-item">
                    <span className="wsnap-item-model">
                      {x.label}
                      {x.code && <span className="wsnap-item-code">{x.code}</span>}
                    </span>
                    {x.status && <span className={`wsnap-chip ${STATUS_CLASS[x.status]}`}>{STATUS_LABEL[x.status]}</span>}
                    <span className="wsnap-item-qty">{x.qty.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {data && (
            <div className="wsnap-legend">
              <span className="wsnap-legend-title">Status:</span>
              <span className="wsnap-chip wsnap-out">Outstanding</span>
              <span className="wsnap-chip wsnap-conf">Confirmed</span>
              <span className="wsnap-chip wsnap-ful">Fulfilled</span>
              <span className="wsnap-generated">Generated {generated} EST</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WeeklySnapshotModal;
